import {
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Flex,
  Heading,
  HStack,
  Input,
  Select,
  Text,
  Textarea,
  Tooltip,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import {
  MdCancel,
  MdExpandLess,
  MdExpandMore,
  MdRefresh,
  MdSave,
} from "react-icons/md";
import { ToolConfig, ToolInputSchema, toolService } from "services/toolService";

interface ToolEditorProps {
  tool: ToolConfig;
  serverId: string;
  onSave: (updatedTool: ToolConfig) => void;
  onCancel: () => void;
}

const DESCRIPTION_MAX_SYMBOLS = 1000;
const TOOL_NAME_MAX_SYMBOLS = 64;
const TOOL_NAME_PATTERN = /^[a-zA-Z0-9_-]+$/;
const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];

const ToolEditor = ({ tool, serverId, onSave, onCancel }: ToolEditorProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState(tool.name);
  const [description, setDescription] = useState(tool.description);
  const [httpMethod, setHttpMethod] = useState(
    (tool.httpMethod ?? "").toUpperCase(),
  );
  const [endpointPath, setEndpointPath] = useState(tool.endpointPath ?? "");
  const [displayName, setDisplayName] = useState(tool.displayName);
  const [riskLevel, setRiskLevel] = useState<"low" | "medium" | "high">(
    tool.riskLevel,
  );
  const [isDestructive, setIsDestructive] = useState(tool.isDestructive);
  const [isBulk, setIsBulk] = useState(tool.isBulk);
  const [requiresApproval, setRequiresApproval] = useState(
    tool.requiresApproval,
  );
  const [inputSchema, setInputSchema] = useState<ToolInputSchema | null>(null);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [expandedProperties, setExpandedProperties] = useState<Set<string>>(
    new Set(),
  );
  const [isActive, setIsActive] = useState(tool.isActive);
  const descriptionLength = description.length;
  const isDescriptionTooLong = descriptionLength > DESCRIPTION_MAX_SYMBOLS;

  const trimmedName = name.trim();
  const nameError = !trimmedName
    ? t("app.servers.tools.errors.nameRequired")
    : trimmedName.length > TOOL_NAME_MAX_SYMBOLS
      ? t("app.servers.tools.errors.nameTooLong", {
          max: TOOL_NAME_MAX_SYMBOLS,
        })
      : !TOOL_NAME_PATTERN.test(trimmedName)
        ? t("app.servers.tools.errors.nameInvalidChars")
        : "";
  const isNameInvalid = nameError !== "";

  const trimmedEndpointPath = endpointPath.trim();
  const methodError = !httpMethod ? t("app.servers.tools.methodRequired") : "";
  const endpointError = !trimmedEndpointPath
    ? t("app.servers.tools.endpointRequired")
    : !trimmedEndpointPath.startsWith("/")
      ? t("app.servers.tools.endpointMustStartWithSlash")
      : "";
  // Generated tools may use a method outside the common five (e.g. HEAD) — keep it selectable.
  const methodOptions = (
    httpMethod && !HTTP_METHODS.includes(httpMethod)
      ? [...HTTP_METHODS, httpMethod]
      : HTTP_METHODS
  ).map((method) => ({
    label: t(`app.servers.httpMethods.${method.toLowerCase()}`, {
      defaultValue: method,
    }),
    value: method,
  }));

  useEffect(() => {
    const schema = toolService.parseInputSchema(tool.inputSchemaJson);
    setInputSchema(schema);
  }, [tool.inputSchemaJson]);

  const handlePropertyDescriptionChange = (
    propertyPath: string,
    newDescription: string,
  ) => {
    if (!inputSchema) return;

    const updatedSchema = { ...inputSchema };
    const pathParts = propertyPath.split(".");

    let current: any = updatedSchema.properties;
    for (let i = 0; i < pathParts.length - 1; i++) {
      if (current[pathParts[i]]?.properties) {
        current = current[pathParts[i]].properties;
      }
    }

    const lastPart = pathParts[pathParts.length - 1];
    if (current[lastPart]) {
      current[lastPart] = { ...current[lastPart], description: newDescription };
    }

    setInputSchema(updatedSchema);
  };

  const togglePropertyExpansion = (propertyPath: string) => {
    const newExpanded = new Set(expandedProperties);
    if (newExpanded.has(propertyPath)) {
      newExpanded.delete(propertyPath);
    } else {
      newExpanded.add(propertyPath);
    }
    setExpandedProperties(newExpanded);
  };

  const renderProperty = (
    key: string,
    property: any,
    path: string = "",
    level: number = 0,
  ) => {
    const fullPath = path ? `${path}.${key}` : key;
    const hasNestedProperties =
      property.properties && Object.keys(property.properties).length > 0;
    const isExpanded = expandedProperties.has(fullPath);
    const paddingLeft = `${level * 1.5}rem`;

    return (
      <Flex key={fullPath} direction="column" mb={3}>
        <Card.Root>
          <Card.Body>
            <Flex mb={2} align="flex-start" justify="space-between">
              <Flex flex={1} direction="column">
                <Flex align="center" gap={2}>
                  {hasNestedProperties && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => togglePropertyExpansion(fullPath)}
                      p={0}
                    >
                      {isExpanded ? <MdExpandLess /> : <MdExpandMore />}
                    </Button>
                  )}
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    color={{ base: "navy.700", _dark: "white" }}
                    style={{
                      paddingLeft: hasNestedProperties ? "0" : paddingLeft,
                    }}
                  >
                    {key}
                  </Text>
                  <Badge
                    bg={{ base: "blue.100", _dark: "blue.900" }}
                    color={{ base: "blue.700", _dark: "blue.300" }}
                    px={2}
                    py={0.5}
                    fontSize="xs"
                  >
                    {property.type}
                  </Badge>
                  {property.items?.type && (
                    <Text
                      fontSize="xs"
                      color={{ base: "gray.600", _dark: "gray.400" }}
                    >
                      {t("app.servers.tools.ofType", {
                        type: property.items.type,
                      })}
                    </Text>
                  )}
                </Flex>
              </Flex>
            </Flex>

            <Flex direction="column" mt={2}>
              <Text
                mb={1}
                fontSize="xs"
                color={{ base: "gray.600", _dark: "gray.400" }}
              >
                {t("app.servers.description")}
              </Text>
              <Textarea
                value={property.description || ""}
                onChange={(e) =>
                  handlePropertyDescriptionChange(fullPath, e.target.value)
                }
                placeholder={t("app.servers.tools.fieldDescriptionPlaceholder")}
                rows={2}
                size="sm"
              />
            </Flex>

            {(property.enum || property.items?.enum) && (
              <Flex direction="column" mt={2}>
                <Text
                  mb={1}
                  fontSize="xs"
                  color={{ base: "gray.600", _dark: "gray.400" }}
                >
                  {t("app.servers.tools.allowedValues")}
                </Text>
                <Flex wrap="wrap" gap={1}>
                  {Array.isArray(property.enum)
                    ? property.enum.map((val: any, idx: number) => (
                        <Badge
                          key={idx}
                          bg={{ base: "gray.100", _dark: "gray.700" }}
                          px={2}
                          py={0.5}
                          fontSize="xs"
                        >
                          {String(val)}
                        </Badge>
                      ))
                    : Array.isArray(property.items?.enum)
                      ? property.items.enum.map((val: any, idx: number) => (
                          <Badge
                            key={idx}
                            bg={{ base: "gray.100", _dark: "gray.700" }}
                            px={2}
                            py={0.5}
                            fontSize="xs"
                          >
                            {String(val)}
                          </Badge>
                        ))
                      : null}
                </Flex>
                {property.items?.description && (
                  <Text
                    mt={1}
                    fontSize="xs"
                    color={{ base: "gray.600", _dark: "gray.400" }}
                    fontStyle="italic"
                  >
                    {property.items.description}
                  </Text>
                )}
              </Flex>
            )}
          </Card.Body>
        </Card.Root>

        {hasNestedProperties && isExpanded && (
          <Flex direction="column" ml={6} mt={2} gap={2}>
            {Object.entries(property.properties).map(
              ([nestedKey, nestedProp]: [string, any]) =>
                renderProperty(nestedKey, nestedProp, fullPath, level + 1),
            )}
          </Flex>
        )}
      </Flex>
    );
  };

  const handleSave = async () => {
    if (isDescriptionTooLong) {
      setError(
        t("app.servers.tools.descriptionTooLong", {
          over: descriptionLength - DESCRIPTION_MAX_SYMBOLS,
          max: DESCRIPTION_MAX_SYMBOLS,
        }),
      );
      return;
    }

    if (isNameInvalid) {
      setError(nameError);
      return;
    }

    if (methodError || endpointError) {
      setError(methodError || endpointError);
      return;
    }

    try {
      setSaving(true);
      setError("");

      const updateRequest = {
        name: trimmedName,
        httpMethod,
        endpointPath: trimmedEndpointPath,
        description,
        inputSchemaJson: inputSchema
          ? toolService.stringifyInputSchema(inputSchema)
          : tool.inputSchemaJson,
        displayName,
        riskLevel,
        isDestructive,
        isBulk,
        requiresApproval,
      };

      const updatedTool = await toolService.updateTool(
        serverId,
        tool.id,
        updateRequest,
      );
      onSave(updatedTool);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          t("app.servers.tools.errors.updateFailed"),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    try {
      setToggling(true);
      setError("");
      setSuccess("");

      const updatedTool = await toolService.toggleToolStatus(
        serverId,
        tool.id,
        !isActive,
      );
      setIsActive(updatedTool.isActive);
      onSave(updatedTool);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          t("app.servers.errors.failedToToggleTool"),
      );
    } finally {
      setToggling(false);
    }
  };

  const handleRegenerateTool = async () => {
    try {
      setRegenerating(true);
      setError("");
      setSuccess("");

      const regeneratedTool = await toolService.regenerateTool(
        serverId,
        tool.id,
      );

      // Update local state with regenerated data
      setName(regeneratedTool.name);
      setDescription(regeneratedTool.description);
      setDisplayName(regeneratedTool.displayName);
      setRiskLevel(regeneratedTool.riskLevel);
      setIsDestructive(regeneratedTool.isDestructive);
      setIsBulk(regeneratedTool.isBulk);
      const schema = toolService.parseInputSchema(
        regeneratedTool.inputSchemaJson,
      );
      setInputSchema(schema);

      setSuccess(t("app.servers.tools.regenerateSuccess"));
      onSave(regeneratedTool);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          t("app.servers.tools.errors.regenerateFailed"),
      );
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <Card.Root>
      <Card.Body>
        <Flex direction="column" mb={6}>
          <Heading
            size="lg"
            mb={2}
            color={{ base: "navy.700", _dark: "white" }}
          >
            {t("app.servers.tools.editTool", { name: tool.name })}
          </Heading>
          <Flex align="center" gap={2}>
            <Badge bg={{ base: "gray.100", _dark: "gray.700" }} px={2} py={1}>
              {tool.httpMethod}
            </Badge>
            <Text fontSize="sm" color={{ base: "gray.600", _dark: "gray.400" }}>
              {tool.endpointPath}
            </Text>
            <Badge
              bg={
                isActive
                  ? { base: "green.100", _dark: "green.900" }
                  : { base: "gray.100", _dark: "gray.700" }
              }
              color={
                isActive
                  ? { base: "green.700", _dark: "green.300" }
                  : { base: "gray.700", _dark: "gray.300" }
              }
              px={2}
              py={1}
            >
              {isActive ? t("app.common.active") : t("app.common.inactive")}
            </Badge>
          </Flex>
        </Flex>

        {error && (
          <Box mb={4} bg="red.100" p={3} borderRadius="lg">
            <Text fontSize="sm" color="red.700">
              {error}
            </Text>
          </Box>
        )}

        {success && (
          <Box mb={4} bg="green.100" p={3} borderRadius="lg">
            <Text fontSize="sm" color="green.700">
              {success}
            </Text>
          </Box>
        )}

        <Flex direction="column" mb={6}>
          <Flex justify="space-between" align="center" mb={2}>
            <Text
              fontSize="sm"
              fontWeight="medium"
              color={{ base: "navy.700", _dark: "white" }}
            >
              {t("app.servers.tools.toolActions")}
            </Text>
            <HStack gap={2}>
              <Button
                onClick={handleRegenerateTool}
                disabled={regenerating || saving || toggling}
                variant="outline"
                colorPalette="purple"
                size="sm"
              >
                <MdRefresh />
                {regenerating
                  ? t("app.servers.tools.regenerating")
                  : t("app.servers.tools.regenerateTool")}
              </Button>
              <Button
                onClick={handleToggleStatus}
                disabled={toggling || saving || regenerating}
                variant="outline"
                colorPalette={isActive ? "red" : "green"}
                size="sm"
              >
                {isActive
                  ? t("app.servers.tools.disableTool")
                  : t("app.servers.tools.enableTool")}
              </Button>
            </HStack>
          </Flex>
          <Text fontSize="xs" color={{ base: "gray.600", _dark: "gray.400" }}>
            {t("app.servers.tools.regenerateDescription")}
            {isActive
              ? t("app.servers.tools.toolActiveDescription")
              : t("app.servers.tools.toolInactiveDescription")}
          </Text>
        </Flex>

        <Flex direction="column" mb={6}>
          <Text
            mb={2}
            fontSize="sm"
            fontWeight="medium"
            color={{ base: "navy.700", _dark: "white" }}
          >
            {t("app.servers.tools.toolDescription")}
          </Text>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("app.servers.tools.descriptionPlaceholder")}
            rows={4}
          />
          <Text
            fontSize="xs"
            color={
              isDescriptionTooLong
                ? "red.600"
                : { base: "gray.600", _dark: "gray.400" }
            }
            mt={1}
          >
            {t("app.servers.tools.descriptionLength", {
              count: descriptionLength,
              max: DESCRIPTION_MAX_SYMBOLS,
            })}
          </Text>
          {isDescriptionTooLong && (
            <Text fontSize="xs" color="red.600" mt={1}>
              {t("app.servers.tools.descriptionTooLong", {
                over: descriptionLength - DESCRIPTION_MAX_SYMBOLS,
                max: DESCRIPTION_MAX_SYMBOLS,
              })}
            </Text>
          )}
        </Flex>

        <Flex direction="column" mb={6}>
          <Text
            mb={2}
            fontSize="sm"
            fontWeight="medium"
            color={{ base: "navy.700", _dark: "white" }}
          >
            {t("app.servers.tools.internalName")}
          </Text>
          <Text
            mb={2}
            fontSize="xs"
            color={{ base: "gray.600", _dark: "gray.400" }}
          >
            {t("app.servers.tools.internalNameHint")}
          </Text>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("app.servers.tools.internalNamePlaceholder")}
            maxLength={TOOL_NAME_MAX_SYMBOLS}
          />
          {isNameInvalid && (
            <Text fontSize="xs" color="red.600" mt={1}>
              {nameError}
            </Text>
          )}
        </Flex>

        <Flex direction="column" mb={6}>
          <Flex gap={4} direction={{ base: "column", md: "row" }}>
            <Flex direction="column" minW={{ md: "160px" }}>
              <Text
                mb={2}
                fontSize="sm"
                fontWeight="medium"
                color={{ base: "navy.700", _dark: "white" }}
              >
                {t("app.servers.tools.httpMethod")}
              </Text>
              <Select
                items={methodOptions}
                value={httpMethod ? [httpMethod] : []}
                onValueChange={({ value }: { value: string[] }) =>
                  setHttpMethod(value[0] ?? "")
                }
                size="sm"
              />
              {methodError && (
                <Text fontSize="xs" color="red.600" mt={1}>
                  {methodError}
                </Text>
              )}
            </Flex>
            <Flex direction="column" flex={1}>
              <Text
                mb={2}
                fontSize="sm"
                fontWeight="medium"
                color={{ base: "navy.700", _dark: "white" }}
              >
                {t("app.servers.tools.endpointPath")}
              </Text>
              <Input
                value={endpointPath}
                onChange={(e) => setEndpointPath(e.target.value)}
                placeholder={t("app.servers.tools.endpointPlaceholder")}
                size="sm"
              />
              {endpointError && (
                <Text fontSize="xs" color="red.600" mt={1}>
                  {endpointError}
                </Text>
              )}
            </Flex>
          </Flex>
          <Text
            mt={2}
            fontSize="xs"
            color={{ base: "gray.600", _dark: "gray.400" }}
          >
            {t("app.servers.tools.endpointEditHint")}
          </Text>
        </Flex>

        <Flex direction="column" mb={6}>
          <Text
            mb={2}
            fontSize="sm"
            fontWeight="medium"
            color={{ base: "navy.700", _dark: "white" }}
          >
            {t("app.servers.tools.displayName")}
          </Text>
          <Text
            mb={2}
            fontSize="xs"
            color={{ base: "gray.600", _dark: "gray.400" }}
          >
            {t("app.servers.tools.displayNameHint")}
          </Text>
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={t("app.servers.tools.displayNamePlaceholder")}
          />
        </Flex>

        <Flex direction="column" mb={6}>
          <Text
            mb={2}
            fontSize="sm"
            fontWeight="medium"
            color={{ base: "navy.700", _dark: "white" }}
          >
            {t("app.servers.tools.riskLevel")}
          </Text>
          <Text
            mb={2}
            fontSize="xs"
            color={{ base: "gray.600", _dark: "gray.400" }}
          >
            {t("app.servers.tools.riskLevelHint")}
          </Text>
          <Select
            items={[
              { label: t("app.servers.tools.riskLevels.low"), value: "low" },
              {
                label: t("app.servers.tools.riskLevels.medium"),
                value: "medium",
              },
              { label: t("app.servers.tools.riskLevels.high"), value: "high" },
            ]}
            value={[riskLevel]}
            onValueChange={({ value }: { value: string[] }) =>
              setRiskLevel(value[0] as "low" | "medium" | "high")
            }
            size="sm"
          />
        </Flex>

        <Flex direction="column" mb={6} gap={3}>
          <Flex align="center" gap={2}>
            <Checkbox
              checked={isDestructive}
              onCheckedChange={(e: { checked: boolean | "indeterminate" }) =>
                setIsDestructive(e.checked === true)
              }
            >
              {t("app.servers.tools.destructive")}
            </Checkbox>
            <Tooltip content={t("app.servers.tools.destructiveTooltip")}>
              <Box
                as="span"
                cursor="help"
                color={{ base: "gray.500", _dark: "gray.400" }}
              >
                ⓘ
              </Box>
            </Tooltip>
          </Flex>
          <Text
            fontSize="xs"
            color={{ base: "gray.600", _dark: "gray.400" }}
            ml={6}
          >
            {t("app.servers.tools.destructiveHint")}
          </Text>

          <Flex align="center" gap={2}>
            <Checkbox
              checked={isBulk}
              onCheckedChange={(e: { checked: boolean | "indeterminate" }) =>
                setIsBulk(e.checked === true)
              }
            >
              {t("app.servers.tools.bulk")}
            </Checkbox>
            <Tooltip content={t("app.servers.tools.bulkTooltip")}>
              <Box
                as="span"
                cursor="help"
                color={{ base: "gray.500", _dark: "gray.400" }}
              >
                ⓘ
              </Box>
            </Tooltip>
          </Flex>
          <Text
            fontSize="xs"
            color={{ base: "gray.600", _dark: "gray.400" }}
            ml={6}
          >
            {t("app.servers.tools.bulkHint")}
          </Text>

          <Flex align="center" gap={2}>
            <Checkbox
              checked={requiresApproval}
              onCheckedChange={(e: { checked: boolean | "indeterminate" }) =>
                setRequiresApproval(e.checked === true)
              }
            >
              {t("app.servers.tools.requiresApproval")}
            </Checkbox>
            <Tooltip content={t("app.servers.tools.requiresApprovalTooltip")}>
              <Box
                as="span"
                cursor="help"
                color={{ base: "gray.500", _dark: "gray.400" }}
              >
                ⓘ
              </Box>
            </Tooltip>
          </Flex>
          <Text
            fontSize="xs"
            color={{ base: "gray.600", _dark: "gray.400" }}
            ml={6}
          >
            {t("app.servers.tools.requiresApprovalHint")}
          </Text>
        </Flex>

        <Flex direction="column" mb={6}>
          <Heading
            size="md"
            mb={3}
            color={{ base: "navy.700", _dark: "white" }}
          >
            {t("app.servers.tools.inputParameters")}
          </Heading>
          {inputSchema &&
          inputSchema.properties &&
          Object.keys(inputSchema.properties).length > 0 ? (
            <Flex direction="column" gap={2}>
              {Object.entries(inputSchema.properties).map(([key, property]) =>
                renderProperty(key, property),
              )}
            </Flex>
          ) : (
            <Text fontSize="sm" color={{ base: "gray.600", _dark: "gray.400" }}>
              {t("app.servers.tools.noInputParameters")}
            </Text>
          )}
        </Flex>

        <HStack gap={3}>
          <Button
            onClick={handleSave}
            disabled={
              saving ||
              isDescriptionTooLong ||
              isNameInvalid ||
              !!methodError ||
              !!endpointError
            }
            variant="solid"
            colorPalette="blue"
            flex={1}
          >
            <MdSave />
            {saving ? t("app.common.saving") : t("app.common.saveChanges")}
          </Button>
          <Button
            onClick={onCancel}
            disabled={saving}
            variant="subtle"
            colorPalette="gray"
            flex={1}
          >
            <MdCancel />
            {t("app.common.cancel")}
          </Button>
        </HStack>
      </Card.Body>
    </Card.Root>
  );
};

export default ToolEditor;
